export class SelectSameColour {
    static isSupported() {
        // Currently the "group" property of shapes pushes the PowerPoint API requirement to 1.8.
        return Office.context.requirements.isSetSupported("PowerPointApi", "1.8");
    }
    static async select(colourUsage) {
        let propertyAddr = null;
        let propertyGetter = null;
        switch (colourUsage) {
            case "shapeFill":
                propertyAddr = "fill/foregroundColor";
                propertyGetter = async (context, shape) => shape.fill.foregroundColor;
                break;
            case "shapeOutline":
                propertyAddr = "lineFormat/color";
                propertyGetter = async (context, shape) => shape.lineFormat.color;
                break;
            case "textFill":
                // Grrrrr... I'd like to be able to just load textFrame/textRange/font/color but
                // that causes an error for groups and lines (determined experimentally so that
                // list might not be exhaustive). So if we're after the text colour we have to do
                // another context sync for every f***ing shape.
                propertyAddr = "type";
                propertyGetter = async function(context, shape) {
                    if (PowerPoint.ShapeType.group !== shape.type && PowerPoint.ShapeType.line !== shape.type) {
                        shape.load(["textFrame/hasText", "textFrame/textRange/font/color"]);
                        await context.sync();
                        if (shape.textFrame.hasText) return shape.textFrame.textRange.font.color;
                    }
                    return "";
                }
                break;
        }
        await PowerPoint.run(async (context) => {
            const pres = context.presentation;
            const shapes = pres.getSelectedShapes();
            shapes.load(["id", propertyAddr]);
            await context.sync();
            if (shapes.items.length === 0) return;
            let colour = null;
            let keyShapeId = null;
            // Get the shape fill colour from each selected shape and if it's the same for all
            // selected shapes that have a fill colour, set "colour" to that colour. Otherwise
            // "colour" will be null.
            for (const shape of shapes.items) {
                keyShapeId = shape.id;
                const thisColour = await propertyGetter(context, shape);
                if (thisColour != "") {
                    if (colour === null) {
                        colour = thisColour;
                    } else if (thisColour !== colour) {
                        colour = null;
                        break;
                    }
                }
            }
            // Find out which top-level group (if any) the "key" shape belongs to (this is
            // necessary because PowerPoint won't allow selections to include shapes from more than
            // one top-level group or top-level shapes and a group).
            const groupStack = [];
            async function findScope(shapes) {
                shapes.load(["id", "type", propertyAddr]);
                await context.sync();
                groupStack.push(shapes);
                for (const shape of shapes.items) {
                    if (shape.id === keyShapeId) {
                        return true;
                    }
                    if (shape.type === PowerPoint.ShapeType.group) {
                        shape.load("group/shapes");
                        await context.sync();
                        if (await findScope(shape.group.shapes)) return true;
                    }
                }
                groupStack.pop();
                return false;
            }
            const slides = pres.getSelectedSlides();
            slides.load("items");
            await context.sync();
            const currentSlide = slides.items[0];
            if (await findScope(currentSlide.shapes)) {
                // We need to know if the selection is at the top level of the slide or inside
                // a top-level group, as we'll need to recurse into sub-groups in the latter
                // case but not the former.
                let topLevel = 1 == groupStack.length;
                // If we didn't find a colour, the "select group" behaviour is going to kick in but
                // this only makes sense if the selected shapes are actually in a group. So bail
                // out now if that's not the case.
                if (colour === null && topLevel) return;
                // Figure out which scope to use for selecting shapes - if no colour was found, the
                // scope will be the exact group that the original selection was in, otherwise it's
                // the top-level group (or the slide itself).
                let scopedShapes = colour === null ? groupStack[groupStack.length - 1] : (topLevel ? groupStack[0] : groupStack[1]);
                const ids = [];
                async function findMatchingShapes(shapes) {
                    shapes.load(["id", "type", propertyAddr]);
                    await context.sync();
                    for (const shape of shapes.items) {
                        // If no colour was found, we select everything in the same group as the
                        // selected shapes that also has no colour. This is kind of dumb but makes
                        // sense for a lot of BaG graphics that have colours set on groups. Sadly
                        // the PowerPoint API doesn't give us access to colours set at the group
                        // level, so this is the best we can do.
                        const shapeColour = await propertyGetter(context, shape);
                        if ((colour === null && shapeColour === "") || shapeColour === colour) {
                            ids.push(shape.id);
                        }
                        if (!topLevel && shape.type === PowerPoint.ShapeType.group) {
                            await findMatchingShapes(shape.group.shapes);
                        }
                    }
                }
                await findMatchingShapes(scopedShapes);
                currentSlide.setSelectedShapes(ids);
                await context.sync();
            }
        });
    }
}
